import { sliceChars } from '../../string-core.js';
function ok() { return { ok: true }; }
function fail(field, message) {
    return { ok: false, errors: [{ field, message }] };
}
const trim = {
    id: 'trim',
    family: 'string',
    label: 'Trim whitespace',
    description: 'Remove leading and/or trailing whitespace.',
    defaultConfig: { side: 'both' },
    evaluate: (input, config) => {
        const s = String(input ?? '');
        const c = config;
        const side = c.side ?? 'both';
        if (side === 'left')
            return s.replace(/^\s+/, '');
        if (side === 'right')
            return s.replace(/\s+$/, '');
        return s.trim();
    },
    validate: (config) => {
        const c = config;
        if (c.side !== undefined && !['left', 'right', 'both'].includes(c.side)) {
            return fail('side', 'side must be "left", "right", or "both"');
        }
        return ok();
    },
    formSchema: [
        { key: 'side', label: 'Side', type: 'select', options: [
                { value: 'both', label: 'Both' },
                { value: 'left', label: 'Left only' },
                { value: 'right', label: 'Right only' },
            ] },
    ],
    sampleSeeds: ['  hello  ', 'no whitespace', '\t  tabs  \n'],
};
const caseOp = {
    id: 'case',
    family: 'string',
    label: 'Change case',
    description: 'Convert to upper, lower, title, or sentence case.',
    defaultConfig: { mode: 'upper' },
    evaluate: (input, config) => {
        const s = String(input ?? '');
        const mode = config.mode;
        switch (mode) {
            case 'upper': return s.toUpperCase();
            case 'lower': return s.toLowerCase();
            case 'title': return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
            case 'sentence': return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
            default: return s;
        }
    },
    validate: (config) => {
        const c = config;
        if (!c.mode || !['upper', 'lower', 'title', 'sentence'].includes(c.mode)) {
            return fail('mode', 'mode must be one of upper / lower / title / sentence');
        }
        return ok();
    },
    formSchema: [
        { key: 'mode', label: 'Mode', type: 'select', required: true, options: [
                { value: 'upper', label: 'UPPERCASE' },
                { value: 'lower', label: 'lowercase' },
                { value: 'title', label: 'Title Case' },
                { value: 'sentence', label: 'Sentence case' },
            ] },
    ],
    sampleSeeds: ['Hello World', 'mixed CASE example', 'lowercase only'],
};
const pad = {
    id: 'pad',
    family: 'string',
    label: 'Pad',
    description: 'Pad to a target length, truncating if longer.',
    defaultConfig: { side: 'left', length: 6, char: '0' },
    evaluate: (input, config) => {
        const s = String(input ?? '');
        const c = config;
        const truncated = s.length > c.length ? s.slice(0, c.length) : s;
        return c.side === 'left' ? truncated.padStart(c.length, c.char) : truncated.padEnd(c.length, c.char);
    },
    validate: (config) => {
        const c = config;
        if (!['left', 'right'].includes(c.side ?? ''))
            return fail('side', 'side must be left or right');
        if (typeof c.length !== 'number' || c.length <= 0)
            return fail('length', 'length must be a positive integer');
        if (!c.char || c.char.length === 0)
            return fail('char', 'char must be at least one character');
        return ok();
    },
    formSchema: [
        { key: 'side', label: 'Side', type: 'select', required: true, options: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
            ] },
        { key: 'length', label: 'Length', type: 'number', required: true },
        { key: 'char', label: 'Pad character', type: 'text', required: true },
    ],
    sampleSeeds: ['5', '42', 'abc'],
};
const padShimFormSchema = pad.formSchema.filter((f) => f.key !== 'side');
const padLeft = {
    id: 'pad_left',
    family: 'string',
    label: 'Pad left (legacy)',
    description: 'Compat shim — equivalent to "Pad" with side="left". Existing chains keep working.',
    defaultConfig: { length: 6, char: '0' },
    evaluate: (input, config, ctx) => pad.evaluate(input, { ...config, side: 'left' }, ctx),
    validate: (config) => pad.validate({ ...config, side: 'left' }),
    formSchema: padShimFormSchema,
    sampleSeeds: pad.sampleSeeds.slice(),
    hiddenFromPicker: true,
};
const padRight = {
    id: 'pad_right',
    family: 'string',
    label: 'Pad right (legacy)',
    description: 'Compat shim — equivalent to "Pad" with side="right". Existing chains keep working.',
    defaultConfig: { length: 6, char: '0' },
    evaluate: (input, config, ctx) => pad.evaluate(input, { ...config, side: 'right' }, ctx),
    validate: (config) => pad.validate({ ...config, side: 'right' }),
    formSchema: padShimFormSchema,
    sampleSeeds: pad.sampleSeeds.slice(),
    hiddenFromPicker: true,
};
const slice = {
    id: 'slice',
    family: 'string',
    label: 'Slice (substring)',
    description: 'Extract a substring by start position and end (or length).',
    defaultConfig: { start: 0, length: 4 },
    evaluate: (input, config) => {
        const c = config;
        const end = c.end !== undefined ? c.end : c.start + (c.length ?? 0);
        return sliceChars(input, c.start, end);
    },
    validate: (config) => {
        const c = config;
        if (typeof c.start !== 'number')
            return fail('start', 'start is required');
        const hasEnd = c.end !== undefined;
        const hasLength = c.length !== undefined;
        if (!hasEnd && !hasLength)
            return fail('end', 'either end or length must be provided');
        if (hasEnd && hasLength)
            return fail('end', 'end and length are mutually exclusive');
        return ok();
    },
    formSchema: [
        { key: 'start', label: 'Start', type: 'number', required: true },
        { key: 'end', label: 'End (or use length)', type: 'number' },
        { key: 'length', label: 'Length (or use end)', type: 'number' },
    ],
    sampleSeeds: ['2026-04-15', 'INV-12345', 'hello world'],
};
const regexReplace = {
    id: 'regex_replace',
    family: 'string',
    label: 'Regex replace',
    description: 'Match a pattern and replace with a literal or capture references ($1, $2…).',
    defaultConfig: { pattern: '', replacement: '', flags: 'g' },
    evaluate: (input, config) => {
        const s = String(input ?? '');
        const c = config;
        const flags = c.flags ?? 'g';
        return s.replace(new RegExp(c.pattern, flags), c.replacement);
    },
    validate: (config) => {
        const c = config;
        if (typeof c.pattern !== 'string' || c.pattern.length === 0) {
            return fail('pattern', 'pattern is required');
        }
        if (typeof c.replacement !== 'string')
            return fail('replacement', 'replacement is required');
        try {
            new RegExp(c.pattern, c.flags ?? 'g');
        }
        catch (e) {
            const m = e instanceof Error ? e.message : String(e);
            return fail('pattern', `invalid regex: ${m}`);
        }
        return ok();
    },
    formSchema: [
        { key: 'pattern', label: 'Pattern (regex)', type: 'text', required: true },
        { key: 'replacement', label: 'Replacement', type: 'text', required: true },
        { key: 'flags', label: 'Flags', type: 'text', helpText: 'g, i, m, s, u, y (default g)' },
    ],
    sampleSeeds: ['INV-12345', '2026-04-15', 'a-b-c'],
};
const defaultIfEmpty = {
    id: 'default_if_empty',
    family: 'string',
    label: 'Default if empty',
    description: 'Use a fallback string when value is null, empty, or whitespace-only.',
    defaultConfig: { literal: '' },
    evaluate: (input, config) => {
        const c = config;
        if (input === null || input === undefined)
            return c.literal;
        const s = String(input);
        return s.trim().length === 0 ? c.literal : s;
    },
    validate: (config) => {
        const c = config;
        if (typeof c.literal !== 'string')
            return fail('literal', 'literal is required');
        return ok();
    },
    formSchema: [
        { key: 'literal', label: 'Fallback value', type: 'text', required: true },
    ],
    sampleSeeds: ['', '   ', 'has value'],
};
export const stringOps = [trim, caseOp, pad, padLeft, padRight, slice, regexReplace, defaultIfEmpty];
//# sourceMappingURL=string-ops.js.map